export enum StorageKeys {
  GxToken = 'gxToken',
  TripToken = 'tripToken',
  TokenExpiry = 'gxTokenExpiry',
  Impersonator = 'impersonator',
  CachedToken = 'cacheToken',
  LastPathname = 'lastPathname',
  DashboardEditLock = 'dashboard.editLock',
  TabId = 'session.tabId',
  DismissedArticles = 'market.dismissedArticles',
  SpotHistoryColumnWidths = 'ui.spotHistoryColumnWidths'
}

export type StorageMap = {
  gxToken: string;
  tripToken: string;
  gxTokenExpiry: number;
  impersonator: string;
  cacheToken: string;
  lastPathname: string;
  'dashboard.editLock': { tabId: string };
  'session.tabId': string;
  'market.dismissedArticles': { articleId: string; publishedAt: number }[];
  'ui.spotHistoryColumnWidths': Record<string, Record<string, number>>;
};
export type StorageKey = keyof StorageMap;

const SAME_TAB = 'gxgo:storage';

const SESSION_KEYS = new Set<StorageKey>([StorageKeys.TripToken, StorageKeys.TabId]);
const storageFor = (key: StorageKey): Storage =>
  SESSION_KEYS.has(key) ? sessionStorage : localStorage;

export const storage = {
  get<K extends StorageKey>(key: K): StorageMap[K] | null {
    const raw = storageFor(key).getItem(key);
    if (raw === null) return null;
    try {
      // Structured values parse here; legacy quote-wrapped strings also un-wrap.
      return JSON.parse(raw) as StorageMap[K];
    } catch {
      // Strings are stored verbatim (see set): a non-JSON token/pathname is valid, not corrupt.
      return raw as StorageMap[K];
    }
  },

  set<K extends StorageKey>(key: K, value: StorageMap[K]): void {
    // Store strings verbatim — tokens go to the backend / into URLs, so quote-wrapping breaks them.
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    storageFor(key).setItem(key, serialized);
    window.dispatchEvent(new CustomEvent(SAME_TAB, { detail: key }));
  },

  remove(key: StorageKey): void {
    storageFor(key).removeItem(key);
    window.dispatchEvent(new CustomEvent(SAME_TAB, { detail: key }));
  },

  subscribe<K extends StorageKey>(key: K, fn: (value: StorageMap[K] | null) => void): () => void {
    const onCross = (e: StorageEvent) => {
      if (e.key === key) fn(storage.get(key));
    };
    const onSame = (e: Event) => {
      if ((e as CustomEvent).detail === key) fn(storage.get(key));
    };
    window.addEventListener('storage', onCross);
    window.addEventListener(SAME_TAB, onSame);
    return () => {
      window.removeEventListener('storage', onCross);
      window.removeEventListener(SAME_TAB, onSame);
    };
  }
};
